import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { Api } from '@/core/api';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { IncidentTagItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { Colors, hexToRgba, isDarkColor } from '@/styles/colors';
import { getIncidentTagColor } from '../components/utils/getIncidentTagColor';
import { useIncidentsTagsData } from './useIncidentsTagsData';

export const QUERY_KEY_INCIDENTS_TAG_COLORS = 'incidents-tag-colors';

export type TagColorsByName = Record<IncidentTagItem['IncidentTagsName'], ReturnType<typeof getColors>>;

const getColors = (color: string): Pick<React.CSSProperties, 'background' | 'border' | 'color'> => {
  const background = hexToRgba(color, 0.7);
  return {
    background,
    border: `1px solid ${color}`,
    color: isDarkColor(background) ? Colors.White : Colors.Black,
  };
};

export const useIncidentsTagColorsData = () => {
  const [tagColorsByName, setTagColors] = useState<TagColorsByName>({});

  const { orgId } = useCurrentUser();
  const { data: tagsData } = useIncidentsTagsData();

  const { data: tagColorsData, ...query } = useQuery({
    queryKey: [QUERY_KEY_INCIDENTS_TAG_COLORS, orgId],
    queryFn: () => Api.mapEvents.getIncidentTagColors({ orgId }),
    placeholderData: [],
  });

  useEffect(() => {
    if (isEmpty(tagColorsData) && tagsData) {
      const colors = tagsData.reduce(
        (acc, tag: IncidentTagItem['IncidentTagsName'], idx) => {
          acc[tag] = getColors(getIncidentTagColor(idx + 1));
          return acc;
        },
        {} as typeof tagColorsByName,
      );

      setTagColors(colors);
    } else if (!isEmpty(tagColorsData)) {
      const colors =
        tagColorsData?.reduce(
          (acc, tag) => {
            acc[tag.IncidentTagsName] = {
              background: hexToRgba(tag.BackgroundColor, 0.8),
              border: `1px solid ${tag.BackgroundColor}`,
              color: isDarkColor(tag.BackgroundColor) ? Colors.White : Colors.Black,
            };
            return acc;
          },
          {} as typeof tagColorsByName,
        ) || {};

      setTagColors(colors);
    }
  }, [tagColorsData, tagsData]);

  return { tagColorsByName, tagColorsData: tagColorsData || [], ...query };
};
